import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  getGiftEventLogList,
  GiftEventLogItem,
  GiftEventType,
} from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForAdminHook } from '@shared/api/giftOrder/giftOrder.hook';
import { Loading } from '@shared/components/Loading';

const formatDate = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const eventLabel: Record<string, string> = {
  courtesy_view: '답례글 페이지',
  store_view: '답례품 목록',
  item_view: '상품 상세',
};

const eventColor: Record<string, string> = {
  courtesy_view: 'bg-blue-100 text-blue-800',
  store_view: 'bg-purple-100 text-purple-800',
  item_view: 'bg-orange-100 text-orange-800',
};

const getDateRange = (range: 'today' | '7d' | '30d' | 'all') => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  let start: Date | undefined;

  switch (range) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      break;
    case '30d':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      break;
    default:
      start = undefined;
  }

  return {
    startDate: start?.toISOString(),
    endDate: end.toISOString(),
  };
};

const FunnelBar: FC<{ label: string; count: number; maxCount: number; color: string }> = ({
  label,
  count,
  maxCount,
  color,
}) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-bold text-gray-900">{count}</span>
      </div>
      <div className="h-8 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`flex h-full items-center rounded-full px-3 text-xs font-bold text-white transition-all duration-500 ${color}`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        >
          {pct >= 10 && `${pct.toFixed(1)}%`}
        </div>
      </div>
    </div>
  );
};

interface FunnelStats {
  courtesy_view: number;
  store_view: number;
  item_view: number;
  purchased: number;
  totalBugos: number;
}

export const GiftEventLogPage: FC = function GiftEventLogPage() {
  const [range, setRange] = useState<'today' | '7d' | '30d' | 'all'>('7d');
  const [logs, setLogs] = useState<GiftEventLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'funnel' | 'logs'>('funnel');

  // 주문 데이터 (purchased 집계용)
  useGiftOrderListForAdminHook('default');
  const orderStatus = useRecoilValue(giftOrderListForAdminStatusAtomFamily('default'));
  const orderList = useRecoilValue(giftOrderListForAdminDataAtomFamily('default'));

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(range);
      const data = await getGiftEventLogList(startDate, endDate);
      setLogs(data);
    } catch (error) {
      console.error('이벤트 로그 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // 프론트에서 퍼널 집계
  const stats: FunnelStats = useMemo(() => {
    const bugoEvents = new Map<string, Set<GiftEventType>>();

    logs.forEach((log) => {
      const existing = bugoEvents.get(log.bugo);
      if (existing) {
        existing.add(log.event);
      } else {
        bugoEvents.set(log.bugo, new Set([log.event]));
      }
    });

    let courtesyView = 0;
    let storeView = 0;
    let itemView = 0;

    bugoEvents.forEach((events) => {
      if (events.has('courtesy_view')) courtesyView++;
      if (events.has('store_view')) storeView++;
      if (events.has('item_view')) itemView++;
    });

    // purchased: 이벤트 로그에 있는 bugo 중 실제 주문이 존재하는 수
    const bugoIdsWithEvents = new Set(bugoEvents.keys());
    let purchased = 0;
    if (orderList) {
      const bugoIdsWithOrders = new Set(
        orderList.map((order) => {
          const bugo = order.bugo;
          return typeof bugo === 'object' && bugo !== null ? bugo._id : String(bugo);
        }),
      );
      bugoIdsWithEvents.forEach((bugoId) => {
        if (bugoIdsWithOrders.has(bugoId)) purchased++;
      });
    }

    return {
      courtesy_view: courtesyView,
      store_view: storeView,
      item_view: itemView,
      purchased,
      totalBugos: bugoEvents.size,
    };
  }, [logs, orderList]);

  const funnelData = useMemo(() => {
    const maxCount = Math.max(
      stats.courtesy_view,
      stats.store_view,
      stats.item_view,
      stats.purchased,
      1,
    );
    return [
      {
        label: '① 답례글 페이지 진입',
        count: stats.courtesy_view,
        maxCount,
        color: 'bg-blue-500',
      },
      {
        label: '② 답례품 목록 진입',
        count: stats.store_view,
        maxCount,
        color: 'bg-purple-500',
      },
      {
        label: '③ 상품 상세 조회',
        count: stats.item_view,
        maxCount,
        color: 'bg-orange-500',
      },
      {
        label: '④ 구매 완료',
        count: stats.purchased,
        maxCount,
        color: 'bg-green-500',
      },
    ];
  }, [stats]);

  const conversionRates = useMemo(() => {
    const rate = (a: number, b: number) => (b > 0 ? ((a / b) * 100).toFixed(1) : '-');
    return [
      {
        label: '답례글 → 답례품 목록',
        value: rate(stats.store_view, stats.courtesy_view),
      },
      {
        label: '답례품 목록 → 상세',
        value: rate(stats.item_view, stats.store_view),
      },
      {
        label: '상세 → 구매',
        value: rate(stats.purchased, stats.item_view),
      },
      {
        label: '전체 전환율 (답례글→구매)',
        value: rate(stats.purchased, stats.courtesy_view),
      },
    ];
  }, [stats]);

  const eventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    logs.forEach((log) => {
      counts[log.event] = (counts[log.event] || 0) + 1;
    });
    return counts;
  }, [logs]);

  if (loading || orderStatus === 'loading' || orderStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">이벤트 로그</h1>
        <p className="mt-1 text-sm text-gray-500">
          답례품 구매 전 사용자 행동 퍼널 분석 (부고 단위 집계)
        </p>
      </div>

      {/* 기간 필터 */}
      <div className="mb-6 flex gap-2">
        {(
          [
            { key: 'today', label: '오늘' },
            { key: '7d', label: '7일' },
            { key: '30d', label: '30일' },
            { key: 'all', label: '전체' },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            onClick={() => setRange(item.key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              range === item.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 탭 */}
      <div className="mb-4 flex border-b border-gray-200">
        <button
          onClick={() => setTab('funnel')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'funnel'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          퍼널 분석
        </button>
        <button
          onClick={() => setTab('logs')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'logs'
              ? 'border-b-2 border-indigo-600 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          로그 목록 ({logs.length})
        </button>
      </div>

      {tab === 'funnel' && (
        <div className="space-y-6">
          {/* 요약 카드 */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="rounded-lg bg-white p-4 shadow">
              <p className="text-xs text-gray-500">고유 부고 수</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{stats.totalBugos}</p>
            </div>
            {funnelData.map((item) => (
              <div key={item.label} className="rounded-lg bg-white p-4 shadow">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{item.count}</p>
              </div>
            ))}
          </div>

          {/* 퍼널 바 차트 */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">퍼널 (부고 단위)</h2>
            <div className="space-y-4">
              {funnelData.map((item) => (
                <FunnelBar key={item.label} {...item} />
              ))}
            </div>
          </div>

          {/* 전환율 */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">단계별 전환율</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {conversionRates.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between rounded-lg border border-gray-100 p-4"
                >
                  <span className="text-sm text-gray-600">{item.label}</span>
                  <span className="text-lg font-bold text-indigo-600">
                    {item.value === '-' ? '-' : `${item.value}%`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 이벤트별 집계 */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-bold text-gray-800">
              이벤트별 총 로그 수 (중복 포함)
            </h2>
            <div className="space-y-2">
              {Object.entries(eventCounts).map(([event, count]) => (
                <div key={event} className="flex items-center justify-between">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      eventColor[event] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {eventLabel[event] || event}
                  </span>
                  <span className="font-bold text-gray-900">{count}건</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  시각
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  이벤트
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  부고 ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상품 ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  세션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    로그가 없습니다.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          eventColor[log.event] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {eventLabel[log.event] || log.event}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">
                      {log.bugo?.slice(-6) || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">
                      {log.giftItem?.slice(-6) || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-400">
                      {log.sessionId?.slice(0, 8) || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
